import { Loading } from "@cpj-ai/panel-npm-package";
import { Card, Input } from "@heroui/react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { FixedSizeList as List } from "react-window";

export interface Option {
  value: string;
  label: string;
  [key: string]: any;
}

interface SearchableSelectorProps {
  options: Option[];
  selectedValue?: string | string[];
  onSelect: (value: string) => void;
  placeholder?: string;
  label?: string;
  loading?: boolean;
  onSearch?: (search: string) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  height?: number;
  virtualized?: boolean;
  gridLayout?: boolean;
  renderOption?: (option: Option, isSelected: boolean) => React.ReactNode;
  containerScrolling?: boolean;
  multiSelect?: boolean;
}

const SearchableSelector = ({
  options,
  selectedValue,
  onSelect,
  placeholder = "Search...",
  label,
  loading = false,
  onSearch,
  hasMore = false,
  onLoadMore,
  height = 300,
  virtualized = false,
  gridLayout = false,
  renderOption,
  containerScrolling = false,
  multiSelect = false,
}: SearchableSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options || []);
  const [isMobileKeyboardOpen, setIsMobileKeyboardOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingSelectionRef = useRef<string | null>(null);

  useEffect(() => {
    if (onSearch) {
      setFilteredOptions(options || []);
    } else {
      setFilteredOptions(
        (options || []).filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase())),
      );
    }
  }, [options, searchTerm, onSearch]);

  // Mobile keyboard detection
  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
          const currentHeight = window.visualViewport?.height || window.innerHeight;
          const standardHeight = window.screen.height;
          const keyboardThreshold = standardHeight * 0.75;
          setIsMobileKeyboardOpen(currentHeight < keyboardThreshold);
        }
      }
    };

    const handleVisualViewportChange = () => {
      handleResize();
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", handleVisualViewportChange);
      }
      handleResize();
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener("resize", handleVisualViewportChange);
        }
      }
    };
  }, []);

  // Handle pending selection after keyboard closes
  useEffect(() => {
    if (!isMobileKeyboardOpen && pendingSelectionRef.current) {
      const pendingValue = pendingSelectionRef.current;
      pendingSelectionRef.current = null;
      setTimeout(() => {
        onSelect(pendingValue);
      }, 100);
    }
  }, [isMobileKeyboardOpen, onSelect]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (onSearch) {
      onSearch(value);
    }
  };

  const isOptionSelected = (optionValue: string): boolean => {
    if (Array.isArray(selectedValue)) {
      return selectedValue.includes(optionValue);
    }
    return selectedValue === optionValue;
  };

  const handleOptionSelect = (optionValue: string) => {
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

    if (isMobile && isMobileKeyboardOpen) {
      // On mobile with keyboard open, store the selection and blur the input
      pendingSelectionRef.current = optionValue;
      if (inputRef.current) {
        inputRef.current.blur();
      }
    } else {
      // Direct selection for desktop or mobile without keyboard
      onSelect(optionValue);
    }
  };

  const handleScroll = useCallback(
    ({ scrollOffset, scrollUpdateWasRequested }: { scrollOffset: number; scrollUpdateWasRequested: boolean }) => {
      if (!scrollUpdateWasRequested && onLoadMore && hasMore && !loading) {
        const isCloseToBottom = scrollOffset > height * 0.8;
        if (isCloseToBottom) {
          onLoadMore();
        }
      }
    },
    [onLoadMore, hasMore, loading, height],
  );

  const Row = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      if (index === filteredOptions.length && hasMore) {
        return (
          <div style={style} className="flex justify-center items-center py-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
          </div>
        );
      }

      const option = filteredOptions[index];
      if (!option) return null;

      const isSelected = isOptionSelected(option.value);

      if (renderOption) {
        return (
          <div style={style} onClick={() => handleOptionSelect(option.value)}>
            <Card
              isPressable
              className={`p-3 w-full h-full flex items-center gap-3 transition-all ${
                isSelected ? "border-primary-500 border-2 bg-primary-50" : "border border-gray-200"
              }`}
            >
              {isSelected && (
                <motion.div
                  className="absolute top-1 right-1 w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                >
                  <svg width="12" height="8" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M1 5L5 9L13 1"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </motion.div>
              )}
              {renderOption(option, isSelected)}
            </Card>
          </div>
        );
      }

      return (
        <div
          style={style}
          className={`px-4 py-3 cursor-pointer transition-colors ${
            isSelected ? "bg-primary-50 text-primary-700" : "hover:bg-gray-50"
          }`}
          onClick={() => handleOptionSelect(option.value)}
        >
          {option.label}
        </div>
      );
    },
    [filteredOptions, handleOptionSelect, hasMore, renderOption, isOptionSelected],
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  return (
    <div className="w-full">
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}

      <Input
        ref={inputRef}
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleSearchChange}
        className="mb-3"
        startContent={
          <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M14.6666 14.6667L10.5 10.5M12.1666 6.58333C12.1666 9.63449 9.6844 12.1167 6.63324 12.1167C3.58207 12.1167 1.09991 9.63449 1.09991 6.58333C1.09991 3.53217 3.58207 1.05 6.63324 1.05C9.6844 1.05 12.1666 3.53217 12.1666 6.58333Z"
              stroke="#6B7280"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        }
      />

      {multiSelect && Array.isArray(selectedValue) && selectedValue.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {selectedValue.map((value) => {
            const option = options.find((opt) => opt.value === value);
            if (!option) return null;

            return (
              <div
                key={value}
                className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-sm flex items-center"
              >
                {option.label}
                <button
                  className="ml-2 text-primary-500 hover:text-primary-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOptionSelect(value);
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {loading && filteredOptions.length === 0 ? (
        <div className="flex justify-center items-center py-8 bg-white">
          <Loading className="py-6" />
        </div>
      ) : !loading && filteredOptions.length === 0 ? (
        <div className="p-4 text-center text-gray-500 bg-white">No results found</div>
      ) : gridLayout ? (
        <div className="bg-white overflow-hidden">
          <motion.div
            className={`grid p-3 grid-cols-2 md:grid-cols-3 gap-3 ${
              !containerScrolling ? "max-h-[400px] overflow-y-auto" : ""
            }`}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredOptions.map((option, index) => {
              const isSelected = isOptionSelected(option.value);
              return (
                <motion.div key={option.value} variants={itemVariants} custom={index} layout>
                  <Card
                    isPressable
                    className={`p-3 w-full h-full flex items-center gap-3 transition-all ${
                      isSelected ? "border-primary-500 border-2 bg-primary-50" : "border border-gray-200"
                    }`}
                    onPress={() => handleOptionSelect(option.value)}
                  >
                    {isSelected && (
                      <motion.div
                        className="absolute top-1 right-1 w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      >
                        <svg width="12" height="8" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M1 5L5 9L13 1"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </motion.div>
                    )}
                    {renderOption ? renderOption(option, isSelected) : option.label}
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>

          {hasMore && loading && (
            <div className="py-4 flex justify-center">
              <Loading className="py-6" />
            </div>
          )}
        </div>
      ) : virtualized ? (
        <div className="bg-white overflow-hidden">
          <List
            height={height}
            itemCount={hasMore ? filteredOptions.length + 1 : filteredOptions.length}
            itemSize={20}
            width="100%"
            onScroll={handleScroll}
          >
            {Row}
          </List>
        </div>
      ) : (
        <div className="bg-white overflow-hidden">
          <div
            className="max-h-[300px] overflow-y-auto"
            onScroll={(e) => {
              if (hasMore && !loading) {
                const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                if (scrollTop + clientHeight >= scrollHeight - 50) {
                  onLoadMore?.();
                }
              }
            }}
          >
            {filteredOptions.map((option) => (
              <motion.div
                key={option.value}
                className={`px-4 py-3 cursor-pointer transition-colors ${
                  isOptionSelected(option.value) ? "bg-primary-50 text-primary-700" : "hover:bg-gray-50"
                }`}
                onClick={() => handleOptionSelect(option.value)}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                {renderOption ? renderOption(option, isOptionSelected(option.value)) : option.label}
              </motion.div>
            ))}
            {hasMore && loading && <Loading className="py-6" />}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelector;
